/**
 * Admin Panel Page - With Sidebar Navigation
 * Role-based access: Staff only see their assigned functions
 */
import { useState, useEffect } from 'react';
import AssistedBookingWidget from '../components/admin/AssistedBookingWidget';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStaffFunctions } from '../context/StaffFunctionsContext';
import { adminAPI, usersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Link2, 
  ArrowLeft, 
  Shield, 
  Users,
  CheckCircle,
  XCircle,
  RefreshCw,
  Database,
  Store,
  Package,
  Truck,
  MapPin,
  Settings, ShieldCheck, ShieldAlert,
  BarChart3,
  FileText,
  Menu,
  X,
  Lock,
  Bell,
  QrCode,
  Mail,
  Sliders,
  ShoppingCart,
  ClipboardList,
  Flag,
  LogOut
} from 'lucide-react';
import { menuCategories, getAccessibleMenu } from '../config/adminMenuConfig';


// Helper to check if item is new (within 120 days)
const isItemNew = (item) => {
  if (!item.isNew || !item.newUntil) return false;
  const untilDate = new Date(item.newUntil);
  return new Date() < untilDate;
};

// Menu items are now defined in ../config/adminMenuConfig.js
// Use menuCategories for categorized menu or getAccessibleMenu(isAdmin, staffFunctions) for filtered menu

const AdminPanel = () => {
  const { user, isAdmin, hasRole, logout } = useAuth();
  const { isAdminUser, hasAccessToItem, myFunctions, loading: functionsLoading, refreshFunctions } = useStaffFunctions();
  const navigate = useNavigate();
  const location = useLocation();
  const [adminTest, setAdminTest] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testLoading, setTestLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Get accessible menu categories based on user's role and functions
  const accessibleMenu = getAccessibleMenu(isAdminUser, myFunctions);
  
  // Flatten to list for compatibility with existing code
  const visibleSidebarItems = accessibleMenu.flatMap(cat => cat.items);

  // Refresh functions when component mounts (in case token was just set)
  useEffect(() => {
    if (user) {
      refreshFunctions();
    }
  }, [user, refreshFunctions]);

  // Track if data has been fetched
  const [dataFetched, setDataFetched] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);

  useEffect(() => {
    // Wait for functions to finish loading
    if (functionsLoading) {
      return;
    }
    
    // Already fetched data, don't re-run checks
    if (dataFetched) {
      return;
    }
    
    const isAdminOrCEO = isAdmin();
    const isStaffUser = hasRole('STAFF');
    
    // Admins and CEOs always have access
    if (isAdminOrCEO) {
      setDataFetched(true);
      fetchData();
      return;
    }
    
    // Staff users need to have functions assigned
    if (isStaffUser && myFunctions.length > 0) {
      setDataFetched(true);
      fetchData();
      return;
    }
    
    // Staff user with no functions - show error only if we're sure functions are loaded
    if (isStaffUser && myFunctions.length === 0 && !functionsLoading) {
      toast.error('No functions assigned. Contact your administrator.');
      navigate('/dashboard');
      return;
    }
    
    // Non-admin, non-staff user
    if (!isAdminOrCEO && !isStaffUser) {
      toast.error('Access denied');
      navigate('/dashboard');
    }
  }, [isAdmin, hasRole, navigate, myFunctions, functionsLoading, isAdminUser, dataFetched]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersData, rolesData, reqCountRes] = await Promise.all([
        usersAPI.list(0, 20),
        adminAPI.getRoles(),
        api.get('/role-requests/admin/count').catch(() => ({ data: { pending_count: 0 } }))
      ]);
      setUsers(usersData);
      setRoles(rolesData);
      setPendingRequests(reqCountRes.data?.pending_count || 0);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminTest = async () => {
    setTestLoading(true);
    try {
      const result = await adminAPI.test();
      setAdminTest(result);
      toast.success('Admin access verified!');
    } catch (error) {
      toast.error('Admin test failed');
    } finally {
      setTestLoading(false);
    }
  };

  const handleSeedRoles = async () => {
    try {
      const result = await adminAPI.seedRoles();
      toast.success(result.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to seed roles');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      ADMIN: 'bg-red-500/20 text-red-400 border-red-500/30',
      CEO: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      DRIVER: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      BUSINESS_MERCHANT: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      CUSTOMER: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      PUDO_POINT: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      FULFILLMENT: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    };
    return colors[role] || colors.CUSTOMER;
  };

  const handleNavClick = (item) => {
    if (item.disabled) {
      toast.info(`${item.label} coming soon`);
      return;
    }
    navigate(item.path);
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-slate-900 border-r border-slate-800 transition-all duration-300 flex flex-col`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Link2 className="h-7 w-7 text-amber-500" />
              <span className="text-lg font-bold text-white">Admin</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation Items - Categorized */}
        <nav className="flex-1 p-2 space-y-4 overflow-y-auto">
          {accessibleMenu.map((category) => (
            <div key={category.id}>
              {sidebarOpen && (
                <h4 className="px-3 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {category.label}
                </h4>
              )}
              <div className="space-y-0.5">
                {category.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.path === location.pathname;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavClick(item)}
                      disabled={item.disabled}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left ${
                        isActive 
                          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' 
                          : item.disabled
                            ? 'text-slate-600 cursor-not-allowed'
                            : 'text-slate-400 hover:text-white hover:bg-slate-800'
                      }`}
                      title={sidebarOpen ? '' : item.label}
                      data-testid={`sidebar-${item.id}`}
                    >
                      <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-amber-400' : ''}`} />
                      {sidebarOpen && (
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate flex items-center gap-2">
                            {item.label}
                            {isItemNew(item) && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full animate-pulse">NEW</span>
                            )}
                          </p>
                          {item.disabled && (
                            <p className="text-xs text-slate-600">Coming soon</p>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          
          {/* Show message if staff has limited access */}
          {!isAdminUser && sidebarOpen && (
            <div className="mt-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center gap-2 text-slate-400 text-xs">
                <Lock className="h-3 w-3" />
                <span>Limited access mode</span>
              </div>
              <p className="text-slate-500 text-xs mt-1">
                You have access to {myFunctions.length} function{myFunctions.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </nav>

        {/* Back to Dashboard */}
        <div className="p-2 border-t border-slate-800">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-5 w-5 flex-shrink-0" />
            {sidebarOpen && <span className="text-sm font-medium">Back to Dashboard</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-40">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">
                {isAdminUser ? 'Admin Panel' : 'Staff Dashboard'}
              </h1>
              <p className="text-slate-400 text-sm">
                {isAdminUser 
                  ? 'System administration and management'
                  : `Access: ${myFunctions.join(', ') || 'Loading...'}`
                }
              </p>
            </div>
            <span className={`text-xs font-mono px-3 py-1.5 rounded border ${
              isAdminUser 
                ? 'text-red-400 bg-red-500/10 border-red-500/20'
                : 'text-blue-400 bg-blue-500/10 border-blue-500/20'
            }`}>
              {isAdminUser ? 'ADMIN' : 'STAFF'}
            </span>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="p-6" data-testid="admin-panel">
          {/* Quick Stats - Admin only */}
          {isAdminUser && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="h-5 w-5 text-blue-400" />
                  <span className="text-slate-400 text-sm">Total Users</span>
                </div>
                <p className="text-2xl font-bold text-white">{users.length}</p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle className="h-5 w-5 text-teal-400" />
                  <span className="text-slate-400 text-sm">Active Users</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {users.filter(u => u.is_active).length}
                </p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Database className="h-5 w-5 text-amber-400" />
                  <span className="text-slate-400 text-sm">System Roles</span>
                </div>
                <p className="text-2xl font-bold text-white">{roles.length}</p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Truck className="h-5 w-5 text-purple-400" />
                  <span className="text-slate-400 text-sm">Drivers</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {users.filter(u => u.roles?.includes('DRIVER')).length}
                </p>
              </div>
            </div>
          )}

          {/* Assisted Booking Operations Widget - Admin only */}
          {isAdminUser && (
            <div className="mb-8">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3">Live Operations</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <AssistedBookingWidget />
              </div>
            </div>
          )}

          {/* Staff Welcome Banner - Staff only */}
          {!isAdminUser && (
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-bold text-white mb-2">Welcome, {user?.name || 'Staff Member'}</h2>
              <p className="text-slate-400 mb-4">
                You have access to the following modules. Click on any item in the sidebar to get started.
              </p>
              <div className="flex flex-wrap gap-2">
                {myFunctions.map(func => (
                  <span key={func} className="px-3 py-1 rounded-full text-sm bg-blue-500/20 text-blue-400 border border-blue-500/30 capitalize">
                    {func}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Admin Test & Roles Section - Admin only */}
          {isAdminUser && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div 
                data-testid="admin-test-card"
                className="bg-slate-900/50 border border-slate-800 rounded-lg p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-5 w-5 text-red-400" />
                  <h3 className="text-sm font-medium text-white uppercase tracking-wide">
                    Admin Access Test
                  </h3>
                </div>
                <p className="text-slate-400 text-sm mb-4">
                  Verify your admin privileges are working correctly
                </p>
                <Button
                  data-testid="run-admin-test-btn"
                  onClick={handleAdminTest}
                  disabled={testLoading}
                  className="bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-md font-medium"
                >
                  {testLoading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Run Test
                </Button>
                {adminTest && (
                  <div className="mt-4 p-3 bg-teal-500/10 border border-teal-500/20 rounded">
                    <p className="text-teal-400 text-sm font-mono">
                      {adminTest.message}
                    </p>
                    <p className="text-slate-400 text-xs mt-1">
                      Roles: {adminTest.roles?.join(', ')}
                    </p>
                  </div>
                )}
              </div>

              <div 
                data-testid="roles-card"
                className="bg-slate-900/50 border border-slate-800 rounded-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Database className="h-5 w-5 text-amber-500" />
                    <h3 className="text-sm font-medium text-white uppercase tracking-wide">
                      System Roles
                    </h3>
                  </div>
                  <Button
                    data-testid="seed-roles-btn"
                    onClick={handleSeedRoles}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {roles.map((role) => (
                    <span 
                      key={role.id}
                      className={`px-2 py-0.5 rounded-full text-xs font-mono border ${getRoleBadgeColor(role.name)}`}
                      title={role.description}
                    >
                      {role.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions - Admin only */}
          {isAdminUser && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <button
                  onClick={() => navigate('/admin/featured-stores')}
                  className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-left hover:border-amber-500/30 hover:bg-slate-800/50 transition-all group"
                  data-testid="quick-action-featured-stores"
                >
                  <Store className="h-8 w-8 text-amber-400 mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="text-white font-medium mb-1">Manage Featured Stores</h3>
                  <p className="text-slate-400 text-sm">Add, remove, or reorder homepage stores</p>
                </button>
                
                <button
                  onClick={() => navigate('/ceo')}
                  className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-left hover:border-purple-500/30 hover:bg-slate-800/50 transition-all group"
                  data-testid="quick-action-ceo"
                >
                  <BarChart3 className="h-8 w-8 text-purple-400 mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="text-white font-medium mb-1">CEO Dashboard</h3>
                  <p className="text-slate-400 text-sm">Executive overview and analytics</p>
                </button>
                
                <button
                  onClick={() => navigate('/admin/finance')}
                  className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-left hover:border-teal-500/30 hover:bg-slate-800/50 transition-all group"
                  data-testid="quick-action-finance"
                >
                  <FileText className="h-8 w-8 text-teal-400 mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="text-white font-medium mb-1">Finance Dashboard</h3>
                  <p className="text-slate-400 text-sm">Financial reports and analytics</p>
                </button>

                <button
                  onClick={() => navigate('/admin/withdrawals')}
                  className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-left hover:border-green-500/30 hover:bg-slate-800/50 transition-all group"
                  data-testid="quick-action-withdrawals"
                >
                  <span className="text-3xl mb-3 block group-hover:scale-110 transition-transform">💰</span>
                  <h3 className="text-white font-medium mb-1">Withdrawals</h3>
                  <p className="text-slate-400 text-sm">Review and approve withdrawal requests</p>
                </button>

                <button
                  onClick={() => navigate('/admin/applications')}
                  className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-left hover:border-blue-500/30 hover:bg-slate-800/50 transition-all group relative"
                  data-testid="quick-action-applications"
                >
                  <FileText className="h-8 w-8 text-blue-400 mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="text-white font-medium mb-1">Applications Review</h3>
                  <p className="text-slate-400 text-sm">Review seller, PUDO, and driver applications</p>
                  {pendingRequests > 0 && (
                    <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">{pendingRequests}</span>
                  )}
                </button>

                <button
                  onClick={() => navigate('/admin/pudo-commission')}
                  className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-left hover:border-amber-500/30 hover:bg-slate-800/50 transition-all group"
                  data-testid="quick-action-pudo-commission"
                >
                  <span className="text-3xl mb-3 block group-hover:scale-110 transition-transform">%</span>
                  <h3 className="text-white font-medium mb-1">PUDO Commission & Emails</h3>
                  <p className="text-slate-400 text-sm">Commission rates and email templates</p>
                </button>

                <button
                  onClick={async () => {
                    if (!window.confirm('Send token system announcement email to ALL sellers?')) return;
                    try {
                      const res = await api.post('/tokens/notify-all-sellers');
                      const sent = res.data?.sent || 0;
                      const failed = res.data?.failed || 0;
                      window.alert('Sent to ' + sent + ' sellers. ' + failed + ' failed.');
                    } catch (e) { window.alert('Error: ' + (e.response?.data?.detail || e.message)); }
                  }}
                  className="bg-gradient-to-br from-amber-900/50 to-orange-900/50 border border-amber-500/30 rounded-lg p-4 text-left hover:border-amber-400/50 hover:shadow-lg hover:shadow-amber-500/20 transition-all group"
                  data-testid="quick-action-notify-sellers"
                >
                  <span className="text-3xl mb-3 block group-hover:scale-110 transition-transform">&#x1F4E7;</span>
                  <h3 className="text-white font-medium mb-1">Notify All Sellers</h3>
                  <p className="text-slate-400 text-sm">Send token system email to all sellers</p>
                </button>

                <button
                  onClick={() => navigate('/admin/events')}
                  className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-lg p-4 text-left hover:border-pink-400/50 hover:shadow-lg hover:shadow-pink-500/20 transition-all group"
                  data-testid="quick-action-events"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-3xl group-hover:scale-110 transition-transform">🎉</span>
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></span>
                      <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" style={{animationDelay: '0.2s'}}></span>
                    </div>
                  </div>
                  <h3 className="text-white font-medium mb-1">Events Management</h3>
                  <p className="text-slate-400 text-sm">Manage events, spotlight colors & display settings</p>
                </button>
              </div>
            </div>
          )}

          {/* Users Table - Admin only */}
          {isAdminUser && (
            <div 
              data-testid="users-table-card"
              className="bg-slate-900/50 border border-slate-800 rounded-lg"
            >
              <div className="p-4 border-b border-slate-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-amber-500" />
                    <h3 className="text-sm font-medium text-white uppercase tracking-wide">
                      Users ({users.length})
                    </h3>
                  </div>
                  <Button
                    data-testid="refresh-users-btn"
                    onClick={fetchData}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                    disabled={loading}
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-800">
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">
                        Name
                      </th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">
                        Email/Phone
                      </th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">
                        Roles
                      </th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide p-4">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr 
                        key={u.id}
                        data-testid={`user-row-${u.id}`}
                        className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
                      >
                        <td className="p-4">
                          <p className="text-white">{u.name}</p>
                          <p className="text-slate-500 text-xs font-mono truncate max-w-[150px]">
                            {u.id}
                          </p>
                        </td>
                        <td className="p-4 text-slate-300 font-mono text-sm">
                          {u.email || u.phone}
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1">
                            {u.roles?.map((role) => (
                              <span 
                                key={role}
                                className={`px-1.5 py-0.5 rounded text-xs font-mono border ${getRoleBadgeColor(role)}`}
                              >
                                {role}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-4">
                          {u.is_active ? (
                            <span className="flex items-center gap-1.5 text-teal-400 text-xs font-mono">
                              <CheckCircle className="h-3 w-3" />
                              ACTIVE
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5 text-red-400 text-xs font-mono">
                              <XCircle className="h-3 w-3" />
                              INACTIVE
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
